import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpContext
} from '@angular/common/http';

import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

import {
  Observable,
  catchError,
  switchMap,
  throwError,
  of,
  shareReplay,
  defer,
  finalize,
  firstValueFrom,
  from,
  map
} from 'rxjs';

import Swal from 'sweetalert2';
import { AuthService } from '../../services/auth-service';
import { AESService } from '../../services/aes.service';
import { SKIP_ERROR_POPUP } from '../error-interceptor/error-interceptor';


/* ================= JWT HELPERS ================= */

function decodeBase64Url(input: string): any {
  input = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = input.length % 4;
  if (pad) input += '='.repeat(4 - pad);
  return JSON.parse(atob(input));
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeBase64Url(token.split('.')[1]);
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}


/* ================= STORAGE HELPERS ================= */

// function getLocalStorage(key: string, isBrowser: boolean): string | null {
//   if (!isBrowser) return null;
//   return localStorage.getItem(key);
// }

function setLocalStorage(key: string, value: string, isBrowser: boolean) {
  if (isBrowser) {
    localStorage.setItem(key, value);
  }
}

function removeLocalStorage(key: string, isBrowser: boolean) {
  if (isBrowser) {
    localStorage.removeItem(key);
  }
}


/* ================= INTERCEPTOR ================= */

let refresh$: Observable<string> | null = null;

export const AppInterceptor: HttpInterceptorFn =
(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const deps: SessionDeps = {
    router: inject(Router),
    authService: inject(AuthService),
    aes: inject(AESService),
    isBrowser: isPlatformBrowser(inject(PLATFORM_ID))
  };
  // Skip login (/auth/v2/login) & refresh endpoints
  if (/\/auth\/(v2\/)?login|\/auth\/refresh-token/.test(req.url)) {
    return next(req);
  }
  // Read localStorage safely
  const raw = deps.isBrowser ? localStorage.getItem('user_details') : null;
  if (!raw) {
    // no session: send the request as it is
    return next(req);
  }
  let session: any;
  let accessToken: string | null = null;
  try {
    session = JSON.parse(raw);
    accessToken = session?.tokens?.accessToken;
  } catch {
    return next(req);
  }
  if (!accessToken) {
    return next(req);
  }
  // Token expired check
  if (isTokenExpired(accessToken)) {
    return refreshAndRetry(req, next, deps, session);
  }
  // Attach token
  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || error.status === 403) {
        return refreshAndRetry(req, next, deps, session);
      }
      return throwError(() => error);
    })
  );
};


/* ================= REFRESH TOKEN ================= */

interface SessionDeps {
  router: Router;
  authService: AuthService;
  aes: AESService;
  isBrowser: boolean;
}

// 1. try the refresh token from localStorage;
// 2. if that is rejected too, ask for the password (username shown) instead of logging out.
// Requests that arrive meanwhile wait for the same new token and are then retried.
function refreshAndRetry(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  deps: SessionDeps,
  session: any
): Observable<HttpEvent<any>> {

  if (!refresh$) {
    refresh$ = defer(() => deps.authService.RefreshToken()).pipe(
      map((res: any) => {
        const data = res?.data ?? res;
        session.tokens.accessToken = data.tokens.accessToken;
        session.tokens.refreshToken = data.tokens.refreshToken;
        setLocalStorage('user_details', JSON.stringify(session), deps.isBrowser);
        return data.tokens.accessToken as string;
      }),
      catchError(() => reauthenticate(session, deps)),
      finalize(() => (refresh$ = null)),
      shareReplay(1)
    );
  }

  return refresh$.pipe(
    switchMap((newToken) => {
      const retryReq = req.clone({
        setHeaders: { Authorization: `Bearer ${newToken}` }
      });
      return next(retryReq);
    })
  );
}


/* ================= SIGN IN AGAIN ================= */

const FORCE_LOGIN_MESSAGE = 'Account is already logged in on another device. Do you want to force login?';

function reauthenticate(session: any, deps: SessionDeps): Observable<string> {
  if (!deps.isBrowser) {
    forceLogout(deps.router, deps.isBrowser);
    return throwError(() => new Error('Session expired'));
  }
  return from(promptPassword(session, deps)).pipe(
    switchMap((token) => {
      if (token) {
        return of(token);
      }
      forceLogout(deps.router, deps.isBrowser);
      return throwError(() => new Error('Session expired'));
    })
  );
}

// "Session expired" dialog: shows who is signed in and asks for the password.
// Resolves with the new access token, or null when the user chooses to log out.
async function promptPassword(session: any, deps: SessionDeps): Promise<string | null> {
  const username = safeDecrypt(deps.aes, session?.user?.username);
  const name = safeDecrypt(deps.aes, session?.user?.name);
  if (!username) {
    // nothing to sign in with: fall back to the login page
    await Swal.fire({
      icon: 'warning',
      title: 'Session Expired',
      text: 'Your session has expired. Please login again.',
      confirmButtonColor: '#0f3e6d',
      allowOutsideClick: false,
      customClass: { container: 'session-reauth' }
    });
    return null;
  }

  const result = await Swal.fire({
    icon: 'warning',
    title: 'Session Expired',
    html:
      '<p class="mb-2">Your session has expired. Enter your password to continue as</p>' +
      `<p class="mb-0"><strong>${escapeHtml(name || username)}</strong>` +
      (name ? `<br><small class="text-muted">${escapeHtml(username)}</small>` : '') +
      '</p>' +
      // The browser fills a saved login into this dialog. Without a username box of its own
      // it puts the username into the nearest text box on the page instead (the patient
      // search box), which then hides the patient list.
      `<input type="text" class="session-reauth-username" name="username" autocomplete="username"
        value="${escapeHtml(username)}" readonly tabindex="-1" aria-hidden="true">`,
    input: 'password',
    inputPlaceholder: 'Password',
    inputAttributes: { autocomplete: 'current-password', 'aria-label': 'Password' },
    confirmButtonText: 'Continue',
    confirmButtonColor: '#0f3e6d',
    showCancelButton: true,
    cancelButtonText: 'Log out',
    allowOutsideClick: false,
    allowEscapeKey: false,
    showLoaderOnConfirm: true,
    customClass: { container: 'session-reauth' },
    inputValidator: (value: string) => (value ? null : 'Please enter your password'),
    preConfirm: async (password: string) => {
      try {
        return await signIn(username, password, false, deps);
      } catch (error: any) {
        Swal.showValidationMessage(error?.message || 'Incorrect password. Please try again.');
        return false;
      }
    }
  });

  if (!result.isConfirmed || !result.value?.tokens?.accessToken) {
    return null;
  }
  // same shape as the login page stores
  setLocalStorage('user_details', JSON.stringify(result.value), deps.isBrowser);
  return result.value.tokens.accessToken;
}

async function signIn(username: string, password: string, forcedLogin: boolean, deps: SessionDeps): Promise<any> {
  const payload = {
    username: deps.aes.encrypt(username),
    password: deps.aes.encrypt(password),
    loginType: 1,
    forcedLogin
  };

  let res: any;
  try {
    res = await firstValueFrom(
      deps.authService.UserLogin(payload, new HttpContext().set(SKIP_ERROR_POPUP, true))
    );
  } catch (error: any) {
    throw new Error(error?.error?.message || 'Incorrect password. Please try again.');
  }

  // the "other device" is this browser's own expired session: resume it here
  if (!res?.success && res?.message === FORCE_LOGIN_MESSAGE && !forcedLogin) {
    return signIn(username, password, true, deps);
  }
  if (!res?.success || !res?.data?.tokens?.accessToken) {
    throw new Error(res?.message || 'Sign-in failed. Please try again.');
  }
  return res.data;
}

function safeDecrypt(aes: AESService, value: any): string {
  if (!value) return '';
  try {
    return String(aes.decrypt(String(value)) || '').trim();
  } catch {
    return '';
  }
}

function escapeHtml(value: string): string {
  const entities: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' };
  return value.replace(/[&<>"']/g, (char) => entities[char]);
}


/* ================= FORCE LOGOUT ================= */

function forceLogout(router: Router, isBrowser: boolean) {
  removeLocalStorage('user_details', isBrowser);
  router.navigate(['/login']);
}
